import React from 'react';
import Card from '../shared/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiSun, FiCalendar, FiClock, FiArrowRight } = FiIcons;

const PRIORITY_CONFIG = {
  immediate: { label: 'Immediate', icon: FiAlertCircle, cls: 'text-rose-400 bg-rose-500/10 border-rose-500/30' },
  today: { label: 'Today', icon: FiSun, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  this_week: { label: 'This Week', icon: FiCalendar, cls: 'text-sky-400 bg-sky-500/10 border-sky-500/30' },
  upcoming: { label: 'Upcoming', icon: FiClock, cls: 'text-slate-400 bg-slate-500/10 border-slate-500/30' },
};

const PRIORITY_ORDER = ['immediate', 'today', 'this_week', 'upcoming'];

const CONFIDENCE_STYLES = {
  high: 'text-emerald-400 bg-emerald-500/10',
  medium: 'text-amber-400 bg-amber-500/10',
  low: 'text-slate-400 bg-slate-500/10',
};

function confidenceLabel(confidence) {
  if (confidence >= 0.75) return 'high';
  if (confidence >= 0.4) return 'medium';
  return 'low';
}

// Placeholder — replace with recommendations generated from the selected
// patient's slice of the unified patient_intelligence_profile once that
// pipeline exists. Same shape as the patient-facing RecommendationsCard,
// but rationale is shown in full since the clinician audience needs it.
const DEFAULT_ITEMS = [
  {
    id: 'c1',
    priority: 'immediate',
    text: 'Repeat observations',
    rationale: 'NEWS2 trend has risen across the last two readings.',
    confidence: 0.9,
  },
  {
    id: 'c2',
    priority: 'today',
    text: 'Review antibiotics',
    rationale: 'Temperature and heart rate remain elevated since this morning.',
    confidence: 0.82,
  },
];

/**
 * Clinician-facing recommendations for the selected patient. Same
 * priority/confidence pattern as the patient dashboard's RecommendationsCard,
 * but shows rationale by default (clinicians need the reasoning, not just
 * the action) and uses clinical language.
 */
const RecommendationPanel = ({ items = DEFAULT_ITEMS }) => {
  const grouped = PRIORITY_ORDER
    .map((key) => ({ key, config: PRIORITY_CONFIG[key], entries: items.filter((i) => i.priority === key) }))
    .filter((group) => group.entries.length > 0);

  return (
    <Card className="space-y-3" title="Recommended Actions">
        <div className="text-black text-lg font-bold">Recommendation For patients</div>
      {grouped.length === 0 ? (
        <p className="text-sm text-slate-500">No actions pending for this patient.</p>
      ) : (
        <div className="space-y-4">
          {grouped.map((group) => (
            <div key={group.key}>
              <span
                className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border mb-2 ${group.config.cls}`}
              >
                <SafeIcon icon={group.config.icon} className="text-xs" /> {group.config.label}
              </span>
              <ul className="space-y-2.5">
                {group.entries.map((item) => {
                  const confLabel = confidenceLabel(item.confidence);
                  return (
                    <li key={item.id} className="border-l-2 border-slate-700 pl-3">
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                          <SafeIcon icon={FiArrowRight} className="text-slate-500" /> {item.text}
                        </span>
                        <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${CONFIDENCE_STYLES[confLabel]}`}>
                          {confLabel}
                        </span>
                      </div>
                      {item.rationale && <p className="text-xs text-slate-500 mt-1 ml-6">{item.rationale}</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RecommendationPanel;